import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { apiClient } from '../lib/api-client';
import { env } from '../config/env';

const JOB_LOG_URL_TEMPLATE =
  'https://github.com/philips-internal/ei-rd-eff-deliverymetrics/actions/runs/{run_id}/job/{job_id}';

type CollectorStatus =
  | 'success'
  | 'failed'
  | 'cancelled'
  | 'queued'
  | 'running'
  | 'unknown';

interface CollectorSubRunApi {
  id: number | string;
  name: string;
  status?: string;
  annotations?: string;
  other_errors?: string;
  duration?: number;
  duration_in_hh_mm_ss?: string;
}

interface CollectorRunApi {
  id: number | string;
  collector_name?: string;
  run_number?: number;
  started_at?: string;
  duration?: number;
  duration_in_hh_mm_ss?: string;
  status?: string;
  sub_collectors?: CollectorSubRunApi[];
}

interface CollectorRunsPayload {
  runs?: CollectorRunApi[];
}

interface CollectorSubRun {
  id: string;
  name: string;
  jobLogUrl: string;
  status: CollectorStatus;
  duration: string;
  issueLog?: string;
  issueCount: number;
  hasOtherErrors: boolean;
}

interface CollectorRun {
  id: string;
  name: string;
  workflowRun: string;
  startedAt: string;
  duration: string;
  status: CollectorStatus;
  subCollectors: CollectorSubRun[];
}

interface CollectorStatsDialogProps {
  open: boolean;
  onClose: () => void;
}

const buildJobLogUrl = (runId: number | string, jobId: number | string) =>
  JOB_LOG_URL_TEMPLATE.replace('{run_id}', String(runId)).replace(
    '{job_id}',
    String(jobId)
  );

const normalizeStatus = (value?: string): CollectorStatus => {
  const normalized = (value || '').toLowerCase();

  if (normalized === 'success' || normalized === 'completed') {
    return 'success';
  }

  if (
    normalized === 'failed' ||
    normalized === 'failure' ||
    normalized === 'error'
  ) {
    return 'failed';
  }

  if (normalized === 'cancelled' || normalized === 'canceled') {
    return 'cancelled';
  }

  if (normalized === 'queued' || normalized === 'pending') {
    return 'queued';
  }

  if (normalized === 'in_progress' || normalized === 'running') {
    return 'running';
  }

  return 'unknown';
};

const formatStatusLabel = (status: CollectorStatus): string =>
  status
    .split('_')
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const toDurationText = (duration?: number, hhmmss?: string): string => {
  if (hhmmss) {
    return hhmmss;
  }

  if (typeof duration !== 'number') {
    return '--';
  }

  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = Math.floor(duration % 60);

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const mapApiRunToUiRun = (run: CollectorRunApi): CollectorRun => ({
  id: String(run.id),
  name: run.collector_name || `collector #${run.run_number ?? '--'}`,
  workflowRun: `workflow run id #${run.id}`,
  startedAt: run.started_at ? new Date(run.started_at).toLocaleString() : '--',
  duration: toDurationText(run.duration, run.duration_in_hh_mm_ss),
  status: normalizeStatus(run.status),
  subCollectors: (run.sub_collectors || []).map(subCollector => {
    const subCollectorStatus = normalizeStatus(subCollector.status);
    const hasOtherErrors = Boolean(subCollector.other_errors?.trim());
    const issueChunks = [subCollector.other_errors, subCollector.annotations]
      .map(value => value?.trim())
      .filter((value): value is string => Boolean(value));

    return {
      id: String(subCollector.id),
      name: subCollector.name,
      jobLogUrl: buildJobLogUrl(run.id, subCollector.id),
      status: subCollectorStatus,
      duration: toDurationText(
        subCollector.duration,
        subCollector.duration_in_hh_mm_ss
      ),
      issueLog: issueChunks.length > 0 ? issueChunks.join('\n\n') : undefined,
      issueCount: issueChunks.length,
      hasOtherErrors,
    };
  }),
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const parseRunsPayload = (value: unknown): CollectorRunsPayload => {
  if (!isRecord(value)) {
    return {};
  }

  if (Array.isArray(value.runs)) {
    return { runs: value.runs as CollectorRunApi[] };
  }

  if (isRecord(value.data) && Array.isArray(value.data.runs)) {
    return { runs: value.data.runs as CollectorRunApi[] };
  }

  return {};
};

const getStatusDotProps = (status: CollectorStatus) => {
  switch (status) {
    case 'success':
      return { bg: '#dcfce7', color: '#166534', char: '✓' };
    case 'failed':
      return { bg: '#fee2e2', color: '#b91c1c', char: '✕' };
    case 'running':
      return { bg: '#fef3c7', color: '#92400e', char: '▶' };
    case 'queued':
      return { bg: '#dbeafe', color: '#1d4ed8', char: '…' };
    case 'cancelled':
      return { bg: '#e2e8f0', color: '#475569', char: '—' };
    default:
      return { bg: '#e2e8f0', color: '#475569', char: '?' };
  }
};

const getStatusSx = (status: CollectorStatus) => {
  switch (status) {
    case 'success':
      return { backgroundColor: '#dcfce7', color: '#166534' };
    case 'failed':
      return { backgroundColor: '#fee2e2', color: '#b91c1c' };
    case 'queued':
      return { backgroundColor: '#dbeafe', color: '#1d4ed8' };
    case 'cancelled':
      return { backgroundColor: '#e2e8f0', color: '#475569' };
    case 'running':
      return { backgroundColor: '#fef3c7', color: '#92400e' };
    default:
      return { backgroundColor: '#f1f5f9', color: '#334155' };
  }
};

export const CollectorStatsDialog: React.FC<CollectorStatsDialogProps> = ({
  open,
  onClose,
}) => {
  const isLocked = env.VITE_ENVIRONMENT === 'production';
  const [environment, setEnvironment] = useState<'staging' | 'production'>(
    (env.VITE_ENVIRONMENT as 'staging' | 'production') || 'staging'
  );
  const [statusFilter, setStatusFilter] = useState<'all' | CollectorStatus>('all');
  const [areOtherRunsExpanded, setAreOtherRunsExpanded] = useState(false);
  const [expandedCollectors, setExpandedCollectors] = useState<string[]>([]);
  const [collectorRuns, setCollectorRuns] = useState<CollectorRun[]>([]);
  const [isRunsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState('');
  
  const latestRun = collectorRuns[0];

  const otherRuns = useMemo(() => collectorRuns.slice(1), [collectorRuns]);

  const visibleOtherRuns = useMemo(() => {
    if (statusFilter === 'all') {
      return otherRuns;
    }

    return otherRuns.filter(run => run.status === statusFilter);
  }, [otherRuns, statusFilter]);

  const runTotals = useMemo(
    () => ({
      all: otherRuns.length,
      success: otherRuns.filter(run => run.status === 'success').length,
      failed: otherRuns.filter(run => run.status === 'failed').length,
    }),
    [otherRuns]
  );

  const toggleCollector = (collectorId: string) => {
    setExpandedCollectors(current =>
      current.includes(collectorId)
        ? current.filter(id => id !== collectorId)
        : [...current, collectorId]
    );
  };

  const renderFilterButton = (
    filter: 'all' | 'success' | 'failed',
    label: string,
    count: number
  ) => {
    const isSelected = statusFilter === filter;

    return (
      <Button
        variant='text'
        size='small'
        disableRipple
        onClick={() => setStatusFilter(filter)}
        sx={{
          minWidth: 0,
          px: 0,
          py: '2px',
          borderRadius: 0,
          borderBottom: isSelected ? '2px solid' : '2px solid transparent',
          borderBottomColor: isSelected ? 'primary.main' : 'transparent',
          color: isSelected ? 'primary.main' : '#475569',
          fontWeight: isSelected ? 700 : 500,
          textTransform: 'none',
          '&:hover': {
            backgroundColor: 'transparent',
            borderBottomColor: isSelected ? 'primary.main' : '#bfdbfe',
            color: 'primary.main',
          },
        }}
      >
        {label} ({count})
      </Button>
    );
  };

  const renderRunCard = (run: CollectorRun) => {
    const subFailed = run.subCollectors.filter(s => s.status === 'failed').length;
    const subSuccess = run.subCollectors.filter(s => s.status === 'success').length;
    const subUnknown = run.subCollectors.filter(s => s.status !== 'failed' && s.status !== 'success').length;
    const subOtherErrors = run.subCollectors.filter(s => s.hasOtherErrors).length;
    const isExpanded = expandedCollectors.includes(run.id);

    return (
      <Paper
        key={run.id}
        variant='outlined'
        sx={{ overflow: 'hidden', backgroundColor: '#f8fafc' }}
      >
        {/* Clickable header */}
        <Stack
          direction='row'
          alignItems='center'
          sx={{
            px: '10px',
            pt: '8px',
            pb: '4px',
            gap: '6px',
            cursor: 'pointer',
            '&:hover': { backgroundColor: '#f1f5f9' },
            transition: 'background-color 0.15s',
          }}
          onClick={() => toggleCollector(run.id)}
        >
          <Typography sx={{ fontSize: '0.7rem', color: '#64748b', flexShrink: 0, lineHeight: 1 }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
          <Typography variant='body2' sx={{ fontWeight: 700 }}>
            {run.name}
          </Typography>
        </Stack>

        {/* Date / duration line */}
        <Stack
          direction='row'
          alignItems='center'
          sx={{ px: '10px', pb: '4px', ml: '18px', gap: '6px' }}
        >
          <Typography variant='caption' color='text.secondary'>
            📅 {run.startedAt}
          </Typography>
          <Typography variant='caption' color='text.secondary'>|</Typography>
          <Typography variant='caption' color='text.secondary'>
            ⏱ Duration {run.duration}
          </Typography>
        </Stack>

        {/* Summary badges */}
        <Stack
          direction='row'
          alignItems='center'
          sx={{ px: '10px', pb: '8px', ml: '18px', gap: '8px' }}
        >
          {subFailed > 0 && (
            <Stack direction='row' alignItems='center' sx={{ gap: '4px' }}>
              <Box sx={{
                width: 16, height: 16, borderRadius: '50%',
                backgroundColor: '#fee2e2', color: '#b91c1c',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.6rem', fontWeight: 700, flexShrink: 0,
              }}>✕</Box>
              <Typography variant='caption' sx={{ fontWeight: 600, color: '#b91c1c' }}>
                {subFailed} Failed
              </Typography>
            </Stack>
          )}
          {subSuccess > 0 && (
            <Stack direction='row' alignItems='center' sx={{ gap: '4px' }}>
              <Box sx={{
                width: 16, height: 16, borderRadius: '50%',
                backgroundColor: '#dcfce7', color: '#166534',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.6rem', fontWeight: 700, flexShrink: 0,
              }}>✓</Box>
              <Typography variant='caption' sx={{ fontWeight: 600, color: '#166534' }}>
                {subSuccess} Success
              </Typography>
            </Stack>
          )}
          {subUnknown > 0 && (
            <Stack direction='row' alignItems='center' sx={{ gap: '4px' }}>
              <Box sx={{
                width: 16, height: 16, borderRadius: '50%',
                backgroundColor: '#e2e8f0', color: '#475569',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.6rem', fontWeight: 700, flexShrink: 0,
              }}>?</Box>
              <Typography variant='caption' sx={{ fontWeight: 600, color: '#475569' }}>
                {subUnknown} Unknown
              </Typography>
            </Stack>
          )}
          {subOtherErrors > 0 && (
            <Stack direction='row' alignItems='center' sx={{ gap: '4px' }}>
              <Box sx={{
                width: 16, height: 16, borderRadius: '50%',
                backgroundColor: '#fffbeb', color: '#92400e',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.6rem', fontWeight: 700, flexShrink: 0,
              }}>!</Box>
              <Typography variant='caption' sx={{ fontWeight: 600, color: '#92400e' }}>
                {subOtherErrors} Jobs with issues
              </Typography>
            </Stack>
          )}
        </Stack>

        {/* Sub-collector rows */}
        {isExpanded && (
          <Box sx={{ borderTop: '1px solid #e2e8f0' }}>
            {run.subCollectors.map((subCollector, idx) => {
              const { color: dotColor, bg: dotBg, char: dotChar } = getStatusDotProps(subCollector.status);
              const hasIssueLog = Boolean(subCollector.issueLog?.trim());
              const shouldShowIssuePanel = hasIssueLog || subCollector.status === 'failed';
              const issuePanelSx =
                subCollector.status === 'failed'
                  ? {
                      border: '1px solid #fecaca',
                      backgroundColor: '#fef2f2',
                      color: '#7f1d1d',
                    }
                  : {
                      border: '1px solid #fde68a',
                      backgroundColor: '#fffbeb',
                      color: '#92400e',
                    };
              return (
                <Box
                  key={subCollector.id}
                  sx={{
                    borderBottom: idx < run.subCollectors.length - 1 ? '1px solid #f1f5f9' : 'none',
                  }}
                >
                  <Stack
                    direction='row'
                    alignItems='center'
                    sx={{ px: '10px', py: '5px', gap: '8px', width: '100%' }}
                  >
                    <Box sx={{
                      width: 20, height: 20, borderRadius: '50%',
                      backgroundColor: dotBg, color: dotColor,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.65rem', fontWeight: 700, flexShrink: 0,
                    }}>
                      {dotChar}
                    </Box>

                    <Link
                      href={subCollector.jobLogUrl}
                      target='_blank'
                      rel='noreferrer'
                      underline='hover'
                      color='inherit'
                      onClick={e => e.stopPropagation()}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px', minWidth: 0 }}
                    >
                      <Typography variant='caption' sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                        {subCollector.name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>↗</Typography>
                    </Link>

                    <Box sx={{ flex: 1 }} />

                    <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {subCollector.duration}
                    </Typography>

                    {subCollector.issueCount > 0 && (
                      <Box
                        sx={{
                          ml: '8px',
                          px: '6px',
                          py: '1px',
                          borderRadius: '10px',
                          backgroundColor: '#fffbeb',
                          color: '#92400e',
                          border: '1px solid #fde68a',
                        }}
                      >
                        <Typography variant='caption' sx={{ fontWeight: 600 }}>
                          Issues: {subCollector.issueCount}
                        </Typography>
                      </Box>
                    )}
                  </Stack>

                  {shouldShowIssuePanel && (
                    <Box
                      component='pre'
                      sx={{
                        mx: '10px',
                        mb: '6px',
                        p: '6px',
                        borderRadius: '3px',
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.65rem',
                        fontFamily: 'monospace',
                        maxHeight: '100px',
                        overflow: 'auto',
                        ...issuePanelSx,
                      }}
                    >
                      {subCollector.issueLog ?? 'No issue details available.'}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>
    );
  };

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const controller = new AbortController();

    const loadCollectorRuns = async () => {
      setRunsLoading(true);
      setRunsError('');

      try { 
        
        const response = await apiClient.get<CollectorRunsPayload>(
          'api/collector-runs?limit=100&environment=' + environment,
          {
            signal: controller.signal,
          }
        );

        const payload = parseRunsPayload(response as unknown);
        const runs = (payload.runs || []).map(mapApiRunToUiRun);
        setCollectorRuns(runs);

        if (runs.length > 0) {
          setExpandedCollectors(current =>
            current.length > 0 ? current : [runs[0].id]
          );
        }
      } catch {
        if (!controller.signal.aborted) {
          setRunsError('Unable to load collector runs. Please check backend API.');
        }
      } finally {
        if (!controller.signal.aborted) {
          setRunsLoading(false);
        }
      }
    };

    loadCollectorRuns();

    return () => {
      controller.abort();
    };
  }, [open, environment]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      scroll='paper'
      aria-labelledby='collector-title'
    >
      <DialogTitle id='collector-title'>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <span>Collector Runs</span>
          {!isLocked && (
            <Stack direction='row' spacing={0.5}>
              <Button
                variant={environment === 'staging' ? 'contained' : 'outlined'}
                size='small'
                onClick={() => setEnvironment('staging')}
              >
                Staging
              </Button>
              <Button
                variant={environment === 'production' ? 'contained' : 'outlined'}
                size='small'
                onClick={() => setEnvironment('production')}
              >
                Production
              </Button>
            </Stack>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={0.75}>
          {isRunsLoading && (
            <Paper variant='outlined' sx={{ p: 1 }}>
              <Typography variant='body2'>Loading collector runs...</Typography>
            </Paper>
          )}

          {!isRunsLoading && runsError && (
            <Alert severity='error' sx={{ py: 0.5 }}>{runsError}</Alert>
          )}

          {!isRunsLoading && !runsError && !latestRun && (
            <Paper variant='outlined' sx={{ p: 1 }}>
              <Typography variant='body2'>No collector runs found.</Typography>
            </Paper>
          )}

          {!isRunsLoading && !runsError && latestRun && renderRunCard(latestRun)}

          {!isRunsLoading && !runsError && otherRuns.length > 0 && (
            <Paper variant='outlined' sx={{ overflow: 'hidden' }}>
              <Button
                fullWidth
                size='small'
                sx={{ justifyContent: 'flex-start', px: '10px', py: '8px', fontWeight: 700 }}
                onClick={() => setAreOtherRunsExpanded(current => !current)}
              >
                {areOtherRunsExpanded ? '▼' : '▶'} Previous runs ({otherRuns.length})
              </Button>

              <Collapse in={areOtherRunsExpanded} unmountOnExit>
                <Stack spacing={0.75} sx={{ p: '10px', pt: 0 }}>
                  <Stack
                    direction='row'
                    spacing={2}
                    sx={{
                      flexWrap: 'wrap',
                      pb: '6px',
                      borderBottom: '1px solid #e2e8f0',
                    }}
                  >
                    {renderFilterButton('all', 'All', runTotals.all)}
                    {renderFilterButton('success', 'Success', runTotals.success)}
                    {renderFilterButton('failed', 'Failed', runTotals.failed)}
                  </Stack>

                  {visibleOtherRuns.length === 0 ? (
                    <Paper variant='outlined' sx={{ p: 1 }}>
                      <Typography variant='body2'>No collector runs found.</Typography>
                    </Paper>
                  ) : (
                    visibleOtherRuns.map(renderRunCard)
                  )}
                </Stack>
              </Collapse>
            </Paper>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
